import { useMutation } from "@apollo/client";
import { UPDATE_APPOINTMENT_SERVICE_WORKER_MUTATION } from "@/graphql/appointments";
import { useGraphQLFormattedErrorHandler } from "@/lib/graphql/error-handler";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useSalonStore } from "@/lib/store/salon";

export interface UseUpdateAppointmentServiceWorkerReturn {
  updateAppointmentServiceWorker: (
    appointmentId: string,
    appointmentServiceId: string,
    workerId: string | null
  ) => Promise<any>;
  isUpdating: boolean;
  error: Error | null;
  reset: () => void;
}

export const useUpdateAppointmentServiceWorker = (): UseUpdateAppointmentServiceWorkerReturn => {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const { processGraphQLFormattedError } = useGraphQLFormattedErrorHandler();

  const [updateAppointmentServiceWorkerMutation] = useMutation(
    UPDATE_APPOINTMENT_SERVICE_WORKER_MUTATION,
    {
      errorPolicy: "all",
      // Refetch relevant queries to update the UI
      refetchQueries: [
        "TodaysAppointments",
        "IncomingAppointments",
        "WaitingToConfirmAppointments",
        "Appointment",
      ],
    }
  );

  const { execute, isLoading, error, reset } = useAsyncAction(
    async (appointmentId: string, appointmentServiceId: string, workerId: string | null) => {
      if (!currentSalon?.id) {
        throw new Error("No salon selected");
      }

      // Convert empty string, "unassigned", or em-dash to null for GraphQL schema compatibility
      const normalizedWorkerId =
        workerId === "" || workerId === "unassigned" || workerId === "–" || workerId === "—"
          ? null
          : workerId;

      const result = await updateAppointmentServiceWorkerMutation({
        variables: {
          salonId: currentSalon.id,
          appointmentId,
          input: {
            appointmentServiceId,
            workerId: normalizedWorkerId,
          },
        },
      });

      if (result.errors?.length) {
        throw new Error(
          result.errors.map((error) => processGraphQLFormattedError(error).message).join("; ")
        );
      }

      return result.data?.updateAppointmentServiceWorker;
    },
    {
      loadingMessage: "Updating service worker...",
      successMessage: "Service worker updated successfully",
      errorMessage: "Failed to update service worker",
    }
  );

  return {
    updateAppointmentServiceWorker: execute,
    isUpdating: isLoading,
    error,
    reset,
  };
};
